import React, { useEffect, useRef } from 'react';

/**
 * HeroVisual — a bespoke, cinematic hero animation for The Tech Reach.
 *
 * Concept: "The Reach" — a live talent network.
 * A dark stage rendered with layered SVG shows a curved globe-grid with
 * pulsing talent nodes across the US, arcs of connection tracing between
 * them, and small floating role-tags (LLM, MLOps, Cloud, Data…) that drift
 * upward. Everything animates in pure SVG/CSS for zero video weight and
 * pixel-perfect quality on any screen.
 */

const NODES = [
  { x: 20, y: 55, label: 'SF' },
  { x: 32, y: 40, label: 'SEA' },
  { x: 44, y: 62, label: 'AUS' },
  { x: 52, y: 45, label: 'CHI' },
  { x: 60, y: 68, label: 'ATL' },
  { x: 70, y: 38, label: 'BOS' },
  { x: 78, y: 55, label: 'NYC' },
  { x: 84, y: 66, label: 'MIA' },
];

const CONNECTIONS = [
  [0, 2], [0, 3], [2, 4], [3, 6], [4, 7], [5, 6], [1, 3], [3, 5], [6, 7],
];

const FLOATING_TAGS = [
  { text: 'LLM Engineer',        x: 8,  delay: 0,   dur: 12 },
  { text: 'MLOps Architect',     x: 22, delay: 3,   dur: 14 },
  { text: 'Cloud (AWS/GCP)',     x: 40, delay: 6,   dur: 13 },
  { text: 'Data Platform',       x: 58, delay: 1.5, dur: 15 },
  { text: 'DevOps · SRE',        x: 74, delay: 8,   dur: 12 },
  { text: 'Full-Stack · React',  x: 88, delay: 4.5, dur: 14 },
];

const HeroVisual = () => {
  const rootRef = useRef(null);

  // Subtle parallax on mouse move (adds cinematic depth)
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = (e.clientX - r.left) / r.width - 0.5;
      const cy = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--px', `${cx * 8}px`);
      el.style.setProperty('--py', `${cy * 8}px`);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div
      ref={rootRef}
      className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/5] bg-[#05070E] group"
      style={{ '--px': '0px', '--py': '0px' }}
    >
      {/* Deep space gradient background */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(1200px 700px at 20% 20%, rgba(34,211,238,0.20), transparent 55%), radial-gradient(900px 600px at 80% 90%, rgba(167,139,250,0.16), transparent 55%), linear-gradient(180deg, #05070E 0%, #030510 100%)',
        }}
      />

      {/* Scanning gradient sweep */}
      <div className="absolute inset-0 opacity-40 mix-blend-screen">
        <div className="hv-sweep absolute inset-0" />
      </div>

      {/* Curved horizon grid */}
      <svg
        viewBox="0 0 100 125"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full"
        style={{ transform: 'translate3d(var(--px), var(--py), 0)', transition: 'transform 0.2s ease-out' }}
      >
        <defs>
          <linearGradient id="hv-line" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#67E8F9" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#A78BFA" stopOpacity="0.7" />
          </linearGradient>
          <radialGradient id="hv-node" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#67E8F9" />
            <stop offset="100%" stopColor="#22D3EE" stopOpacity="0" />
          </radialGradient>
          <filter id="hv-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="1.6" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Latitude arcs */}
        <g fill="none" stroke="rgba(34, 211, 238, 0.14)" strokeWidth="0.15">
          {[35, 45, 55, 65, 75, 85, 95].map((y, i) => (
            <path key={i} d={`M -5 ${y} Q 50 ${y - 6} 105 ${y}`} />
          ))}
        </g>

        {/* Longitude arcs */}
        <g fill="none" stroke="rgba(34, 211, 238, 0.10)" strokeWidth="0.15">
          {[15, 30, 45, 60, 75, 90].map((x, i) => (
            <path key={i} d={`M ${x} 20 Q ${50} ${60 + (x - 50) * 0.02} ${x + (x - 50) * 0.15} 110`} />
          ))}
        </g>

        {/* Connection arcs between nodes */}
        <g fill="none" stroke="url(#hv-line)" strokeWidth="0.25" strokeLinecap="round">
          {CONNECTIONS.map(([a, b], i) => {
            const A = NODES[a], B = NODES[b];
            const mx = (A.x + B.x) / 2;
            const my = Math.min(A.y, B.y) - 8;
            const total = 120;
            return (
              <path
                key={i}
                d={`M ${A.x} ${A.y} Q ${mx} ${my} ${B.x} ${B.y}`}
                strokeDasharray={`${total} ${total}`}
                strokeDashoffset={total}
                style={{
                  animation: `hv-draw 3.5s cubic-bezier(.5,.1,.2,1) ${i * 0.35}s infinite`,
                  opacity: 0.85,
                }}
              />
            );
          })}
        </g>

        {/* Travelling pulses along the strongest arcs */}
        <g fill="#67E8F9" filter="url(#hv-glow)">
          {CONNECTIONS.slice(0, 5).map(([a, b], i) => {
            const A = NODES[a], B = NODES[b];
            const mx = (A.x + B.x) / 2;
            const my = Math.min(A.y, B.y) - 8;
            const id = `hv-path-${i}`;
            return (
              <React.Fragment key={i}>
                <path id={id} d={`M ${A.x} ${A.y} Q ${mx} ${my} ${B.x} ${B.y}`} fill="none" />
                <circle r="0.5">
                  <animateMotion dur={`${3.2 + i * 0.4}s`} repeatCount="indefinite" begin={`${i * 0.6}s`}>
                    <mpath href={`#${id}`} />
                  </animateMotion>
                </circle>
              </React.Fragment>
            );
          })}
        </g>

        {/* Talent nodes */}
        <g filter="url(#hv-glow)">
          {NODES.map((n, i) => (
            <g key={i}>
              <circle
                cx={n.x}
                cy={n.y}
                r="2.2"
                fill="url(#hv-node)"
                style={{ animation: `hv-pulse 2.8s ease-in-out ${i * 0.25}s infinite` }}
              />
              <circle cx={n.x} cy={n.y} r="0.75" fill="#67E8F9" />
              <text
                x={n.x}
                y={n.y - 3.2}
                fontSize="1.5"
                fill="rgba(255,255,255,0.55)"
                textAnchor="middle"
                fontFamily="'Space Grotesk', sans-serif"
                fontWeight="600"
                letterSpacing="0.1em"
              >
                {n.label}
              </text>
            </g>
          ))}
        </g>
      </svg>

      {/* Floating role tags rising up */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {FLOATING_TAGS.map((t, i) => (
          <div
            key={i}
            className="absolute hv-tag glass rounded-full px-3 py-1 border border-cyan-400/25 text-[10px] text-cyan-100/90 whitespace-nowrap"
            style={{
              left: `${t.x}%`,
              bottom: '-40px',
              animation: `hv-rise ${t.dur}s linear ${t.delay}s infinite`,
            }}
          >
            <span className="w-1 h-1 rounded-full bg-cyan-400 inline-block mr-1.5 align-middle" />
            {t.text}
          </div>
        ))}
      </div>

      {/* Foreground overlays */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#05070E] via-[#05070E]/20 to-transparent pointer-events-none" />

      {/* Live status card */}
      <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
        <div className="glass rounded-full pl-2.5 pr-3.5 py-1.5 flex items-center gap-2">
          <span className="relative flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-cyan-400 animate-ping opacity-75" />
            <span className="relative rounded-full bg-cyan-400 w-2 h-2" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.2em] text-cyan-200 font-medium">Live · Nationwide network</span>
        </div>
        <div className="glass rounded-full px-3 py-1.5 text-[10px] font-mono text-white/60 hidden sm:block">
          {new Date().getFullYear()} · US
        </div>
      </div>

      {/* Bottom KPI strip */}
      <div className="absolute bottom-5 left-5 right-5 glass rounded-xl p-3.5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] text-cyan-300 uppercase tracking-widest font-medium">Talent in motion</div>
            <div className="text-sm text-white font-semibold mt-0.5">128 roles being filled this week</div>
          </div>
          <div className="flex -space-x-2">
            {[0,1,2,3].map(i => (
              <div key={i} className="w-7 h-7 rounded-full border-2 border-[#05070E] bg-gradient-to-br from-cyan-400 to-violet-500 flex items-center justify-center text-[10px] font-bold text-[#05070E]">
                {['A','J','M','+'][i]}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Local styles for animations */}
      <style>{`
        @keyframes hv-pulse {
          0%, 100% { opacity: 0.4; r: 1.8; transform-box: fill-box; }
          50%      { opacity: 1;   r: 3.2; }
        }
        @keyframes hv-draw {
          0%   { stroke-dashoffset: 120; opacity: 0; }
          20%  { opacity: 0.9; }
          60%  { stroke-dashoffset: 0;   opacity: 0.9; }
          100% { stroke-dashoffset: -120; opacity: 0; }
        }
        @keyframes hv-rise {
          0%   { transform: translateY(0)     scale(0.95); opacity: 0; }
          10%  { opacity: 0.9; }
          85%  { opacity: 0.9; }
          100% { transform: translateY(-620px) scale(1.05); opacity: 0; }
        }
        .hv-sweep {
          background: linear-gradient(120deg, transparent 40%, rgba(34, 211, 238, 0.10) 50%, transparent 60%);
          background-size: 200% 200%;
          animation: hv-sweep-move 6s linear infinite;
        }
        @keyframes hv-sweep-move {
          0%   { background-position: 200% 200%; }
          100% { background-position: -100% -100%; }
        }
      `}</style>
    </div>
  );
};

export default HeroVisual;
